import React from 'react';
import { DonationRecord, LanguageCode } from '../types';
import { Download, HeartHandshake, CreditCard, Building, DollarSign, Calendar, FileText, RefreshCw } from 'lucide-react';

interface DonationHistoryTableProps {
  donations: DonationRecord[];
  currentLang?: LanguageCode;
  onDownloadReceipt?: (receiptNo: string) => void;
}

export const DonationHistoryTable: React.FC<DonationHistoryTableProps> = ({
  donations,
  onDownloadReceipt,
}) => {
  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    const symbol = currency === 'TRY' ? '₺' : currency === 'USD' ? '$' : '€';
    return `${symbol}${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getMethodBadge = (don: DonationRecord) => {
    const method = don.paymentMethod || '';
    if (don.isRecurring || method.toLowerCase().includes('automatic') || method.toLowerCase().includes('otomatik')) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold">
          <RefreshCw className="w-3 h-3 text-[#D4AF37]" />
          <span>Otomatik POS Ödemesi</span>
        </span>
      );
    }

    switch (method.toLowerCase()) {
      case 'credit card':
      case 'kredi kartı':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[11px] font-semibold">
            <CreditCard className="w-3 h-3" /> Kredi Kartı
          </span>
        );
      case 'sepa':
      case 'sepa havale':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[11px] font-semibold">
            <Building className="w-3 h-3" /> SEPA
          </span>
        );
      case 'paypal':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[11px] font-semibold">
            <DollarSign className="w-3 h-3" /> PayPal
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-500/10 text-gray-300 border border-gray-500/20 text-[11px] font-semibold">
            {method}
          </span>
        );
    }
  };

  const handleDownload = (receiptNo: string) => {
    if (onDownloadReceipt) {
      onDownloadReceipt(receiptNo);
    } else {
      alert(`Makbuz #${receiptNo} PDF olarak bilgisayarınıza indirildi.`);
    }
  };

  if (!donations || donations.length === 0) {
    return (
      <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center space-y-3">
        <HeartHandshake className="w-10 h-10 text-gray-400 mx-auto opacity-50" />
        <p className="text-sm font-semibold text-gray-300">Henüz kayıtlı bir bağışınız bulunmamaktadır.</p>
        <p className="text-xs text-gray-400">Kültürel projelere katkıda bulunmak için Bağış Yap sayfasını ziyaret edebilirsiniz.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Table for Tablet / Desktop View */}
      <div className="hidden sm:block overflow-x-auto rounded-2xl border border-white/10 bg-black/20 backdrop-blur-md">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-white/5 text-[11px] font-extrabold uppercase tracking-wider text-[#D4AF37]">
              <th scope="col" className="py-3.5 px-4">Proje / Kampanya</th>
              <th scope="col" className="py-3.5 px-4">Tutar</th>
              <th scope="col" className="py-3.5 px-4">Tarih</th>
              <th scope="col" className="py-3.5 px-4">Ödeme Yöntemi</th>
              <th scope="col" className="py-3.5 px-4">Makbuz No</th>
              <th scope="col" className="py-3.5 px-4 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-xs text-gray-200">
            {donations.map((don) => (
              <tr key={don.id} className="hover:bg-white/5 transition-colors">
                <td className="py-3.5 px-4 font-semibold text-white">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />
                    <span>{don.campaign}</span>
                  </div>
                </td>
                <td className="py-3.5 px-4 font-extrabold text-emerald-400 text-sm">
                  {formatCurrency(don.amount, don.currency)}
                </td>
                <td className="py-3.5 px-4 text-gray-300 font-mono text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span>{don.date}</span>
                  </div>
                </td>
                <td className="py-3.5 px-4">
                  {getMethodBadge(don)}
                </td>
                <td className="py-3.5 px-4 font-mono text-xs text-[#D4AF37]">
                  {don.receiptNo}
                </td>
                <td className="py-3.5 px-4 text-right">
                  <button
                    onClick={() => handleDownload(don.receiptNo)}
                    className="btn-secondary text-[11px] py-1.5 px-3 h-auto min-w-0"
                    title="Makbuz İndir"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Makbuz</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards View for Mobile Screens */}
      <div className="sm:hidden space-y-3">
        {donations.map((don) => (
          <div
            key={don.id}
            className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-bold text-white text-sm leading-snug">{don.campaign}</h4>
                <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <span>{don.date}</span>
                </p>
              </div>
              <span className="font-extrabold text-emerald-400 text-base shrink-0">
                {formatCurrency(don.amount, don.currency)}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs pt-2 border-t border-white/5">
              <div className="space-y-1">
                <div>{getMethodBadge(don)}</div>
                <p className="text-[10px] text-[#D4AF37] font-mono">No: {don.receiptNo}</p>
              </div>

              <button
                onClick={() => handleDownload(don.receiptNo)}
                className="btn-secondary text-xs py-1.5 px-3 h-auto min-w-0"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Makbuz</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
